import i18n from 'i18next';
import EVENTS from '../constants/events';
import { ASAP_TIME } from '../libs/polygon-ordering/src/constants';
import { logEvent } from '../utils/analytics';
import {
  getDeliveryAddressFromPlaceIdAsync,
  getGeocodingDetailsAsync,
} from '../libs/polygon-utils/src/GoogleMapsApiHandler';
import { cloneDeep } from 'lodash';
import { OrderingConstants } from 'polygon-ordering';

const { SALE_TYPE } = OrderingConstants;

interface ModalTitleParams {
  page?: DeliveryAddressFormPage;
  adjustingPin?: boolean;
  selectedAddress?: Partial<DeliveryAddress>;
  addressSelectionView?: DeliveryAddressFormSelectionView;
}

export interface ContinueButtonDisabledParams {
  keyOrderPropertyUpdateInProgress: boolean;
  submittingForm: boolean;
  page: DeliveryAddressFormPage;
  effectiveDeliveryAddress: DeliveryAddress | null;
  deliveryEstimateFailed: boolean | null;
  deliveryRequiresLocationSelection: boolean | null;
  selectedLocationId?: string | null;
  adjustingPin: boolean;
  selectedAddress?: Partial<DeliveryAddress>;
  addressSelectionView: DeliveryAddressFormSelectionView;
  deliveryTimeSelected: boolean;
  saleType: SALE_TYPE;
}

export interface ContinueButtonTextParams {
  page?: DeliveryAddressFormPage;
  addressSelectionView: DeliveryAddressFormSelectionView;
  adjustingPin: boolean;
  editingAddress?: DeliveryAddress;
  selectedAddress?: Partial<DeliveryAddress>;
}

export interface BackButtonParams {
  page: DeliveryAddressFormPage;
  addressSelectionView: DeliveryAddressFormSelectionView;
  editingAddress?: DeliveryAddress;
  adjustingPin: boolean;
  addressInput?: string;
}

export interface ContinueButtonParams {
  getDeliveryAddressFromPlaceIdAsyncFn: typeof getDeliveryAddressFromPlaceIdAsync;
  getGeocodingDetailsAsyncFn: typeof getGeocodingDetailsAsync;
  page: DeliveryAddressFormPage;
  deliveryReadyToApply: boolean;
  deliveryRequiresLocationSelection: boolean | null;
  selectedLocationId: string | null;
  saleType?: SALE_TYPE;
  effectiveDeliveryTime: string;
  effectiveDeliveryAddress: Partial<DeliveryAddress> | null;
  addressSelectionView: DeliveryAddressFormSelectionView;
  editingAddress?: Partial<DeliveryAddress>;
  selectedAddress?: Partial<DeliveryAddress>;
  googleMapsApiKey: string | null;
  adjustingPin: boolean;
  pinCoordinates?: PositionCoordinates;
  recentAddresses: Partial<DeliveryAddress>[];
}

type ActionType =
  | 'resetForm'
  | 'updateForm'
  | 'closeModal'
  | 'setSelectedAddress'
  | 'adjustOrder'
  | 'applyBufferDeliveryEstimate'
  | 'confirmLocationEstimate'
  | 'setDeliveryNotes'
  | 'setDeliveryDetails'
  | 'setDesiredDeliveryAddress'
  | 'saveAddress'
  | 'addToRecentLocations'
  | 'clearRecentLocations'
  | 'setInlineAlert'
  | 'setBufferDeliveryTime';

export type DeliveryDetailsModalAction = {
  type: ActionType;
  payload?: Partial<DeliveryAddressForm> | Partial<DeliveryAddress> | any;
};

/**
 * Returns the appropriate modal title text based on the current modal state.
 *
 * @param params - An object containing the modal state parameters:
 *   - `page`: The current page of the modal.
 *   - `adjustingPin`: Whether the user is adjusting the delivery pin.
 *   - `selectedAddress`: The currently selected address, if any.
 *   - `addressSelectionView`: The current view for address selection.
 * @returns The localized modal title string.
 */
export const getModalTitleText = (params: ModalTitleParams): string => {
  const { page, adjustingPin, selectedAddress, addressSelectionView } = params;

  switch (page) {
    case 'AddressLookupAndConfirmation':
      if (adjustingPin) {
        return i18n.t('deliveryAddress.modalTitleAdjustPin');
      }

      if (!selectedAddress || addressSelectionView === 'Lookup') {
        return i18n.t('deliveryAddress.modalTitleEnterDeliveryAddress');
      }

      break;

    case 'DeliveryDetailsConfirmation':
      return i18n.t('deliveryAddress.modalTitleAddDeliveryAddress');
  }

  return i18n.t('deliveryAddress.modalTitleDelivery');
};

/**
 * Determines whether the "Continue" button should display a loading state.
 *
 * Returns `true` if either a key order property update is in progress or the form is being submitted.
 *
 * @param params - Partial parameters containing the loading state flags.
 * @param params.keyOrderPropertyUpdateInProgress - Indicates if a key order property update is currently in progress.
 * @param params.submittingForm - Indicates if the form is currently being submitted.
 * @returns `true` if the button should show a loading state, otherwise `false`.
 */
export const isContinueButtonLoading = (params: Partial<ContinueButtonDisabledParams>): boolean => {
  const {
    keyOrderPropertyUpdateInProgress,
    submittingForm,
    page,
    addressSelectionView,
    adjustingPin,
  } = params;

  if (page === 'AddressLookupAndConfirmation' && addressSelectionView === 'Map' && !adjustingPin) {
    return false;
  }

  return !!keyOrderPropertyUpdateInProgress || !!submittingForm;
};

/**
 * Determines whether the "Continue" button should be disabled based on the current state of the delivery details modal.
 *
 * @param params - An object containing the state parameters required to evaluate the button's disabled status.
 * @param params.keyOrderPropertyUpdateInProgress - Indicates if a key order property update is currently in progress.
 * @param params.submittingForm - Indicates if the form is currently being submitted.
 * @param params.page - The current page of the modal, which affects the logic for disabling the button.
 * @param params.effectiveDeliveryAddress - The effective delivery address, if available.
 * @param params.deliveryEstimateFailed - Indicates if the delivery estimate has failed.
 * @param params.deliveryRequiresLocationSelection - Indicates if delivery requires a location to be selected.
 * @param params.selectedLocationId - The ID of the selected location, if any.
 * @param params.adjustingPin - Indicates if the user is currently adjusting the delivery pin.
 * @param params.selectedAddress - The address selected by the user, if any.
 * @param params.deliveryTimeSelected - Indicates if a delivery time has been selected.
 * @param params.saleType - The selected sale type. For the delivery details modal this will be either DELIVERY or CATERING_DELIVERY
 * @returns `true` if the "Continue" button should be disabled; otherwise, `false`.
 */
export const isContinueButtonDisabled = (params: ContinueButtonDisabledParams): boolean => {
  const {
    keyOrderPropertyUpdateInProgress,
    submittingForm,
    page,
    effectiveDeliveryAddress,
    deliveryEstimateFailed,
    deliveryRequiresLocationSelection,
    selectedLocationId,
    adjustingPin,
    selectedAddress,
    deliveryTimeSelected,
    saleType,
  } = params;

  if (keyOrderPropertyUpdateInProgress || submittingForm) {
    return true;
  }

  switch (page) {
    case 'DeliveryTimeAndAddressSelection':
      if (!deliveryTimeSelected && saleType === SALE_TYPE.CATERING_DELIVERY) {
        return true;
      }

      if (
        !effectiveDeliveryAddress ||
        deliveryEstimateFailed ||
        (deliveryRequiresLocationSelection && !selectedLocationId)
      ) {
        return true;
      }
      break;

    case 'AddressLookupAndConfirmation':
      if (adjustingPin) {
        return false;
      }

      return selectedAddress === undefined;
  }

  return false;
};

/**
 * Returns the appropriate text for the "Continue" button in the delivery details modal
 * based on the current page and state of the modal.
 *
 * @param params - An object containing the current state and context for the modal.
 * @param params.page - The current page of the modal ('AddressLookupAndConfirmation' or 'DeliveryDetailsConfirmation').
 * @param params.addressSelectionView - The current address selection view ('Map' or other).
 * @param params.adjustingPin - Whether the user is currently adjusting the map pin.
 * @param params.editingAddress - The address that the user is editing.
 * @param params.selectedAddress - The currently selected address, if any.
 * @returns The localized string to display on the "Continue" button.
 */
export const getContinueButtonText = (params: ContinueButtonTextParams) => {
  const { page, addressSelectionView, adjustingPin, editingAddress, selectedAddress } = params;

  switch (page) {
    case 'AddressLookupAndConfirmation':
      if (addressSelectionView === 'Map' && !adjustingPin) {
        return i18n.t('deliveryAddress.buttonConfirmLocation');
      }

      break;

    case 'DeliveryDetailsConfirmation':
      if (editingAddress) {
        return i18n.t('deliveryAddress.buttonUpdateAddress');
      }

      if (selectedAddress?.addressType) {
        return i18n.t('deliveryAddress.buttonSaveAddress');
      }

      return i18n.t('deliveryAddress.buttonAddAddress');
  }

  return i18n.t('deliveryAddress.buttonContinue');
};

/**
 * Generates a sequence of actions to perform when the back button is pressed in the delivery details modal,
 * based on the current page and form state.
 *
 * @param params - An object containing the current state of the modal, including:
 *   - `page`: The current page in the modal flow.
 *   - `addressSelectionView`: The current view for address selection ('Lookup' or 'Map').
 *   - `editingAddress`: The address that the user is editing.
 *   - `adjustingPin`: Whether the user is adjusting the map pin.
 *   - `addressInput`: The current address input value.
 * @returns An array of `BackButtonAction` objects describing the actions to perform.
 */
export const getBackButtonActionPlan = (params: BackButtonParams): DeliveryDetailsModalAction[] => {
  const { page, addressSelectionView, editingAddress, adjustingPin, addressInput } = params;

  const actions: DeliveryDetailsModalAction[] = [];

  switch (page) {
    case 'DeliveryTimeAndAddressSelection':
      actions.push({ type: 'resetForm' });
      actions.push({ type: 'closeModal' });
      break;

    case 'AddressLookupAndConfirmation':
      if (addressSelectionView === 'Lookup') {
        let payload: Partial<DeliveryAddressForm> = {};
        if (!editingAddress) {
          payload.selectedAddress = undefined;
        }
        payload = {
          ...payload,
          addressInput: undefined,
          addressSuggestions: [],
          addressSelectionView: 'Map',
          selectedAddress: cloneDeep(editingAddress),
          editingAddress: undefined,
        };
        actions.push({ type: 'updateForm', payload });
        break;
      }

      if (addressSelectionView === 'Map') {
        actions.push({ type: 'setInlineAlert', payload: { visible: false } });

        if (adjustingPin) {
          actions.push({
            type: 'updateForm',
            payload: { adjustingPin: false, resetMapCenter: true },
          });
          break;
        }

        if (editingAddress) {
          actions.push({ type: 'updateForm', payload: { page: 'DeliveryDetailsConfirmation' } });
          break;
        }

        if (addressInput) {
          actions.push({ type: 'updateForm', payload: { addressSelectionView: 'Lookup' } });
          break;
        }
      }

      actions.push({ type: 'resetForm' });
      break;

    case 'DeliveryDetailsConfirmation':
      if (editingAddress) {
        actions.push({
          type: 'updateForm',
          payload: { editingAddress: undefined, page: 'DeliveryTimeAndAddressSelection' },
        });
        break;
      }

      actions.push({ type: 'updateForm', payload: { page: 'AddressLookupAndConfirmation' } });
  }

  return actions;
};

/**
 * Generates an action plan for the "Continue" button in the Delivery Details Modal based on the current page and form state.
 *
 * The returned actions array contains a sequence of actions to be dispatched, which may include updating the form,
 * confirming delivery details, applying delivery estimates, setting delivery notes, handling address lookup and confirmation,
 * and saving or updating the selected delivery address.
 *
 * The logic varies depending on the current modal page:
 * - On 'DeliveryTimeAndAddressSelection', it handles order adjustment, delivery estimate confirmation, and driver notes.
 * - On 'AddressLookupAndConfirmation', it manages address selection via lookup or map, including geocoding and pin adjustment.
 * - On 'DeliveryDetailsConfirmation', it saves or adds the selected address and sets it as the desired delivery address.
 *
 * @param params - The parameters required to determine the action plan, including page state, delivery details, address info, and Google Maps API key.
 * @returns A promise that resolves to an array of `DeliveryDetailsModalAction` objects representing the actions to be performed.
 */
export const getContinueButtonActionPlan = async (
  params: ContinueButtonParams,
  planType: 'preActions' | 'actions' = 'actions',
) => {
  const {
    getDeliveryAddressFromPlaceIdAsyncFn,
    getGeocodingDetailsAsyncFn,
    page,
    deliveryReadyToApply,
    deliveryRequiresLocationSelection,
    selectedLocationId,
    saleType,
    effectiveDeliveryTime,
    effectiveDeliveryAddress,
    addressSelectionView,
    editingAddress,
    selectedAddress,
    googleMapsApiKey,
    adjustingPin,
    pinCoordinates,
    recentAddresses,
  } = params;

  const actions: DeliveryDetailsModalAction[] = [];

  if (planType === 'preActions') {
    if (page === 'AddressLookupAndConfirmation' && addressSelectionView === 'Map' && adjustingPin) {
      actions.push({ type: 'updateForm', payload: { submittingForm: true } });
    }
    return actions;
  }

  switch (page) {
    case 'DeliveryTimeAndAddressSelection':
      if (deliveryReadyToApply || deliveryRequiresLocationSelection) {
        actions.push({
          type: 'adjustOrder',
          payload: {
            saleType,
            locationId: selectedLocationId!,
            confirmLocationDeliveryEstimate: true,
            isASAP: effectiveDeliveryTime === ASAP_TIME,
          },
        });
        actions.push({ type: 'setBufferDeliveryTime', payload: effectiveDeliveryTime });
      }

      if (deliveryReadyToApply) {
        logEvent(EVENTS.CONFIRM_DELIVERY_DETAILS);
        actions.push({ type: 'applyBufferDeliveryEstimate' });
      } else {
        if (deliveryRequiresLocationSelection && selectedLocationId) {
          actions.push({
            type: 'confirmLocationEstimate',
            payload: {
              locationId: selectedLocationId,
              isASAP: effectiveDeliveryTime === ASAP_TIME,
            },
          });
        } else {
          logEvent(EVENTS.CHECK_DELIVERY_DETAILS);
        }
      }

      if (effectiveDeliveryAddress?.deliveryNotes) {
        actions.push({
          type: 'setDeliveryNotes',
          payload: effectiveDeliveryAddress.deliveryNotes || '',
        });
        logEvent(EVENTS.CHANGE_DRIVER_NOTES);
      }

      actions.push({
        type: 'setDeliveryDetails',
        payload: {
          buildingInformation: effectiveDeliveryAddress?.buildingInformation,
          deliveryInstruction: effectiveDeliveryAddress?.deliveryInstruction,
        },
      });

      break;

    case 'AddressLookupAndConfirmation':
      if (addressSelectionView === 'Lookup') {
        if (selectedAddress === undefined) {
          break;
        }

        if (selectedAddress.addressId) {
          actions.push(
            {
              type: 'setDesiredDeliveryAddress',
              payload: selectedAddress,
            },
            { type: 'resetForm' },
          );
          break;
        }

        if (googleMapsApiKey && selectedAddress.placeId) {
          const address = await getDeliveryAddressFromPlaceIdAsyncFn(
            fetch,
            googleMapsApiKey,
            selectedAddress.placeId,
          );
          actions.push({ type: 'setSelectedAddress', payload: address });
        }

        actions.push({
          type: 'updateForm',
          payload: { addressSelectionView: 'Map' },
        });
      }

      if (addressSelectionView === 'Map') {
        if (adjustingPin) {
          if (!googleMapsApiKey || !pinCoordinates) {
            break;
          }

          const address = await getGeocodingDetailsAsyncFn(
            fetch,
            googleMapsApiKey,
            pinCoordinates.latitude,
            pinCoordinates.longitude,
          );

          address.location = pinCoordinates;

          if (editingAddress) {
            address.addressId = selectedAddress?.addressId;
          }

          actions.push(
            {
              type: 'setSelectedAddress',
              payload: address,
            },
            { type: 'updateForm', payload: { adjustingPin: false, submittingForm: false } },
          );

          break;
        }

        actions.push({ type: 'updateForm', payload: { page: 'DeliveryDetailsConfirmation' } });
      }

      break;

    case 'DeliveryDetailsConfirmation': {
      if (selectedAddress === undefined) {
        break;
      }

      actions.push({ type: 'updateForm', payload: { submittingForm: true } });

      const address = { ...selectedAddress };

      if (editingAddress) {
        address.addressId = editingAddress.addressId;
      }

      if (address.addressType) {
        actions.push({ type: 'saveAddress', payload: { deliveryAddress: address } });
        if (recentAddresses.length > 0 && recentAddresses[0].placeId === address.placeId) {
          actions.push({ type: 'clearRecentLocations' });
        }
      } else {
        actions.push({ type: 'addToRecentLocations', payload: address });
      }

      actions.push(
        {
          type: 'setDesiredDeliveryAddress',
          payload: address,
        },
        { type: 'resetForm' },
      );
    }
  }

  return actions;
};
